<script setup lang="ts">
import { computed } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import type { SteppedProps } from "./SteppedProps";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import Stat from "./components/Stat.vue";

const props = defineProps<{
    block: PageBlock<SteppedProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <FullSpacing>
            <div class="max-w-2xl lg:max-w-7xl mx-auto">
                <div class="max-w-2xl">
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :block-id="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :textColor="styleValues.text"
                            class="mb-4 text-4xl sm:text-5xl"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :block-id="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :textColor="styleValues.text"
                            class="opacity-70"
                        />
                    </EditableElement>
                </div>
                <div class="flex flex-col lg:flex-row lg:items-end gap-8 mx-auto mt-16 lg:mt-20">
                    <EditableElement
                        v-if="!dataValues.firstStat.isHidden"
                        :block-id="block.id"
                        field-name="firstStat"
                    >
                        <Stat
                            :stat="dataValues.firstStat.data.stat"
                            :title="dataValues.firstStat.data.title"
                            :description="dataValues.firstStat.data.description"
                            backgroundColor="#F8FAFC"
                            :titleTextColor="styleValues.text"
                            :descriptionTextColor="styleValues.text"
                            class="flex-none lg:w-72 sm:max-w-md sm:w-3/4"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.secondStat.isHidden"
                        :block-id="block.id"
                        field-name="secondStat"
                    >
                        <Stat
                            :stat="dataValues.secondStat.data.stat"
                            :title="dataValues.secondStat.data.title"
                            :description="dataValues.secondStat.data.description"
                            backgroundColor="#0F172A"
                            titleTextColor="#ffffff"
                            descriptionTextColor="#E0E3EB"
                            class="lg:gap-y-44 w-full lg:max-w-sm"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.thirdStat.isHidden"
                        :block-id="block.id"
                        field-name="thirdStat"
                    >
                        <Stat
                            :stat="dataValues.thirdStat.data.stat"
                            :title="dataValues.thirdStat.data.title"
                            :description="dataValues.thirdStat.data.description"
                            :backgroundColor="styleValues.brand"
                            titleTextColor="#ffffff"
                            descriptionTextColor="#E0E3EB"
                            class="lg:gap-y-28 w-full sm:max-w-xl"
                        />
                    </EditableElement>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>
